import { tmdb } from './tmdb';
import { WatchlistItem, DislikedItem } from './store';
import { UserInteraction, getUserInteractions, getUserLanguagePreferences } from './user-analytics';

export interface ContentItem {
  id: number;
  type: 'movie' | 'tv';
  title?: string;
  name?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  overview: string;
  genre_ids?: number[];
  genres?: { id: number; name: string }[];
  vote_average: number;
  vote_count: number;
  popularity: number;
  release_date?: string;
  first_air_date?: string;
  original_language: string;
  spoken_languages?: { iso_639_1: string; name: string }[];
  keywords?: { id: number; name: string }[];
  credits?: {
    cast: { id: number; name: string; character: string }[];
    crew: { id: number; name: string; job: string }[];
  };
  production_companies?: { id: number; name: string; logo_path?: string | null }[];
  networks?: { id: number; name: string; logo_path?: string | null }[];
  created_by?: { id: number; name: string }[];
  media_type?: 'movie' | 'tv';
  score?: number;
  reason?: string;
}

export interface RecommendationConfig {
  weights: {
    keywordSimilarity: number;
    collaborativeFiltering: number;
    networkStudio: number;
    castCrewMatch: number;
    genreMatch: number;
  };
  filters: {
    minVoteCount: number;
    minRating: number;
    languagePreferred: string[];
  };
  limits: {
    maxResults: number;
    diversityThreshold: number;
  };
}

const defaultConfig: RecommendationConfig = {
  weights: {
    keywordSimilarity: 0.30,      // Thematic keyword matching (highest priority)
    collaborativeFiltering: 0.25, // TMDB recommendations endpoint (user behavior)
    networkStudio: 0.20,          // Network/Studio/Production company matching
    castCrewMatch: 0.15,          // Cast, crew, director matching
    genreMatch: 0.10,             // Genre matching (lowest priority)
  },
  filters: {
    minVoteCount: 50,
    minRating: 5.0,
    languagePreferred: ['en'],    // Moved from weights to filters
  },
  limits: {
    maxResults: 20,
    diversityThreshold: 0.4,     // Increased for more variety
  },
};

export class RecommendationEngine {
  private config: RecommendationConfig;
  private userInteractions: UserInteraction[] = [];
  private userLanguages: string[] = [];
  private genreAffinities: Map<number, number> = new Map();
  private watchedContentCache: Set<number> = new Set();

  constructor(config: Partial<RecommendationConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  async initialize(userId: string, watchlist: WatchlistItem[], dislikedItems: DislikedItem[]) {
    // Load user interactions and preferences
    this.userInteractions = await getUserInteractions(userId);
    this.userLanguages = await getUserLanguagePreferences(userId);

    // Build watched content cache
    watchlist.forEach(item => this.watchedContentCache.add(item.id));
    dislikedItems.forEach(item => this.watchedContentCache.add(item.id));

    // Calculate genre affinities
    this.calculateGenreAffinities(watchlist);
  }

  private calculateGenreAffinities(watchlist: WatchlistItem[]) {
    const genreScores = new Map<number, { score: number; count: number }>();

    watchlist.forEach(item => {
      const recencyBoost = this.getRecencyBoost(item.addedAt);
      const engagementScore = item.personalRank ? item.personalRank / 10 : 0.5;

      item.genres?.forEach(genreId => {
        const current = genreScores.get(genreId) || { score: 0, count: 0 };
        genreScores.set(genreId, {
          score: current.score + (engagementScore * recencyBoost),
          count: current.count + 1,
        });
      });
    });

    // Normalize scores
    genreScores.forEach((value, key) => {
      this.genreAffinities.set(key, value.score / value.count);
    });
  }

  private getRecencyBoost(dateStr: string): number {
    const daysAgo = (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24);
    if (daysAgo < 7) return 1.5;
    if (daysAgo < 30) return 1.2;
    if (daysAgo < 90) return 1.0;
    if (daysAgo < 180) return 0.8;
    return 0.6;
  }

  async getPersonalizedRecommendations(
    currentContent?: ContentItem,
    category: 'trending' | 'similar' | 'genre' | 'mixed' | 'network' | 'collaborative' | 'keywords' | 'cast_crew' | 'personalized' = 'mixed',
    watchlist?: WatchlistItem[]
  ): Promise<ContentItem[]> {
    let candidates: ContentItem[] = [];

    switch (category) {
      case 'trending':
        candidates = await this.getTrendingContent();
        break;
      case 'similar':
        if (currentContent) {
          candidates = await this.getSimilarContent(currentContent);
        }
        break;
      case 'genre':
        candidates = await this.getGenreBasedContent();
        break;
      case 'network':
        if (currentContent) {
          candidates = await this.getNetworkStudioContent(currentContent);
        }
        break;
      case 'collaborative':
        if (currentContent) {
          candidates = await this.getCollaborativeContent(currentContent);
        }
        break;
      case 'keywords':
        if (currentContent) {
          candidates = await this.getKeywordBasedContent(currentContent);
        }
        break;
      case 'cast_crew':
        if (currentContent) {
          candidates = await this.getCastCrewContent(currentContent);
        }
        break;
      case 'personalized':
        if (watchlist && watchlist.length > 0) {
          // Fully scored, filtered and balanced internally — the generic
          // re-scoring below would wash out the watch-history signal
          return this.getPersonalizedForYou(watchlist);
        }
        candidates = await this.getMixedContent();
        break;
      case 'mixed':
      default:
        candidates = await this.getMixedContent();
        break;
    }

    // Score and filter candidates
    const scored = candidates
      .filter(item => !this.watchedContentCache.has(item.id))
      .filter(item => item.id !== currentContent?.id) // Exclude the reference content itself
      .map(item => this.scoreContent(item, currentContent))
      .filter(item => item.score && item.score > 0);

    // Sort by score and diversify
    scored.sort((a, b) => (b.score || 0) - (a.score || 0));
    const diversified = this.diversifyResults(scored);

    return diversified.slice(0, this.config.limits.maxResults);
  }

  private scoreContent(item: ContentItem, referenceContent?: ContentItem): ContentItem {
    let score = 0;
    let reasons: string[] = [];

    // Apply basic quality filters first (return 0 score if fails)
    if (item.vote_count < this.config.filters.minVoteCount) {
      return { ...item, score: 0, reason: 'Insufficient votes' };
    }
    if (item.vote_average < this.config.filters.minRating) {
      return { ...item, score: 0, reason: 'Low rating' };
    }

    // Language filter (prefer user's language, but don't exclude others)
    const languageMatch = this.config.filters.languagePreferred.includes(item.original_language);
    if (!languageMatch && this.config.filters.languagePreferred.length > 0) {
      score *= 0.7; // Slight penalty for non-preferred language
    }

    // If no reference content, use generic scoring
    if (!referenceContent) {
      // Generic scoring based on quality and popularity
      const ratingScore = this.calculateRatingScore(item);
      const popularityScore = this.calculatePopularityScore(item);
      score = (ratingScore * 0.6) + (popularityScore * 0.4);
      reasons.push('Recommended for you');
      return { ...item, score, reason: reasons[0] };
    }

    // NEW ALGORITHM: Multi-tier scoring based on thematic relevance

    // Tier 1: Keyword/Thematic Similarity (30%)
    const keywordScore = this.calculateKeywordScore(item, referenceContent);
    score += keywordScore * this.config.weights.keywordSimilarity;
    if (keywordScore > 0.5) {
      reasons.push('Similar themes');
    }

    // Tier 2: Collaborative Filtering Score (25%) - items get bonus if from collaborative endpoint
    // This is handled by having collaborative recommendations in the candidate pool
    // We give a flat boost to items that came from the collaborative endpoint
    if (item.score === 999) { // Special marker we'll set for collaborative items
      const collaborativeBonus = 0.25;
      score += collaborativeBonus;
      reasons.push('Viewers also watched');
      item.score = undefined; // Remove marker
    }

    // Tier 3: Network/Studio/Production Company (20%)
    const networkStudioScore = this.calculateNetworkStudioScore(item, referenceContent);
    score += networkStudioScore * this.config.weights.networkStudio;
    if (networkStudioScore > 0.5) {
      if (referenceContent.type === 'tv' && item.type === 'tv') {
        reasons.push('Same network');
      } else if (referenceContent.type === 'movie' && item.type === 'movie') {
        reasons.push('Same studio');
      } else {
        reasons.push('Same production team');
      }
    }

    // Tier 4: Cast/Crew Connections (15%)
    const castCrewScore = this.calculateCastCrewScore(item, referenceContent);
    score += castCrewScore * this.config.weights.castCrewMatch;
    if (castCrewScore > 0.4) {
      reasons.push('Same cast/crew');
    }

    // Tier 5: Genre Matching (10%) - lowest priority
    const genreScore = this.calculateGenreScore(item);
    score += genreScore * this.config.weights.genreMatch;

    // Quality boost (doesn't count toward 100%, but boosts good content)
    const qualityMultiplier = 1 + (this.calculateRatingScore(item) * 0.2);
    score *= qualityMultiplier;

    return {
      ...item,
      score,
      reason: reasons[0] || 'Recommended for you',
    };
  }

  private calculateNetworkStudioScore(item: ContentItem, reference: ContentItem): number {
    if (!item.production_companies && !item.networks) return 0;
    if (!reference.production_companies && !reference.networks) return 0;

    let score = 0;

    // For TV shows, prioritize network matching
    if (item.type === 'tv' && reference.type === 'tv') {
      const itemNetworks = new Set((item.networks || []).map(n => n.id));
      const refNetworks = new Set((reference.networks || []).map(n => n.id));

      let networkMatches = 0;
      itemNetworks.forEach(id => { if (refNetworks.has(id)) networkMatches++; });

      if (networkMatches > 0 && refNetworks.size > 0) {
        score = Math.min(networkMatches / refNetworks.size, 1.0);
        return score;
      }
    }

    // For movies or as fallback, check production companies
    const itemCompanies = new Set((item.production_companies || []).map(c => c.id));
    const refCompanies = new Set((reference.production_companies || []).map(c => c.id));

    if (itemCompanies.size === 0 || refCompanies.size === 0) return score;

    let companyMatches = 0;
    itemCompanies.forEach(id => { if (refCompanies.has(id)) companyMatches++; });

    // If there's a match in production companies, return high score
    if (companyMatches > 0) {
      score = Math.min((companyMatches / Math.min(itemCompanies.size, refCompanies.size)) * 0.8, 1.0);
    }

    return score;
  }

  private calculateGenreScore(item: ContentItem): number {
    const itemGenres = item.genre_ids || item.genres?.map(g => g.id) || [];
    if (itemGenres.length === 0) return 0.5;

    let totalScore = 0;
    let matchCount = 0;

    itemGenres.forEach(genreId => {
      const affinity = this.genreAffinities.get(genreId);
      if (affinity !== undefined) {
        totalScore += affinity;
        matchCount++;
      }
    });

    if (matchCount === 0) return 0.3; // Unknown genre
    return Math.min(totalScore / matchCount, 1.0);
  }

  private calculateRatingScore(item: ContentItem): number {
    if (!item.vote_average || item.vote_count < 10) return 0.5;

    // Normalize rating (0-10 scale to 0-1)
    const normalizedRating = item.vote_average / 10;

    // Apply vote count weight (more votes = more reliable)
    const voteWeight = Math.min(Math.log10(item.vote_count) / 4, 1.0);

    return normalizedRating * voteWeight;
  }

  private calculatePopularityScore(item: ContentItem): number {
    if (!item.popularity) return 0.5;

    // Log scale for popularity (typical range: 0-1000+)
    const logPopularity = Math.log10(item.popularity + 1);
    const normalizedPopularity = Math.min(logPopularity / 3, 1.0);

    // Apply time decay to popularity
    const releaseDate = item.release_date || item.first_air_date;
    if (releaseDate) {
      const monthsOld = (Date.now() - new Date(releaseDate).getTime()) / (1000 * 60 * 60 * 24 * 30);
      const decay = Math.exp(-monthsOld / 24); // Half-life of 2 years
      return normalizedPopularity * (0.5 + 0.5 * decay);
    }

    return normalizedPopularity;
  }

  private calculateRecencyScore(item: ContentItem): number {
    const releaseDate = item.release_date || item.first_air_date;
    if (!releaseDate) return 0.5;

    const daysOld = (Date.now() - new Date(releaseDate).getTime()) / (1000 * 60 * 60 * 24);

    if (daysOld < 30) return 1.0;   // Brand new
    if (daysOld < 90) return 0.8;   // Recent
    if (daysOld < 365) return 0.6;  // This year
    if (daysOld < 730) return 0.4;  // Last year
    if (daysOld < 1825) return 0.2; // Last 5 years
    return 0.1; // Older content
  }

  private calculateCastCrewScore(item: ContentItem, reference: ContentItem): number {
    if (!item.credits || !reference.credits) return 0;

    const itemCast = new Set(item.credits.cast.slice(0, 10).map(c => c.id));
    const itemCrew = new Set(
      item.credits.crew
        .filter(c => ['Director', 'Producer', 'Writer'].includes(c.job))
        .map(c => c.id)
    );

    const refCast = new Set(reference.credits.cast.slice(0, 10).map(c => c.id));
    const refCrew = new Set(
      reference.credits.crew
        .filter(c => ['Director', 'Producer', 'Writer'].includes(c.job))
        .map(c => c.id)
    );

    let matches = 0;
    itemCast.forEach(id => { if (refCast.has(id)) matches++; });
    itemCrew.forEach(id => { if (refCrew.has(id)) matches += 2; }); // Weight crew more

    const maxPossible = Math.min(itemCast.size + itemCrew.size * 2, 20);
    return maxPossible > 0 ? matches / maxPossible : 0;
  }

  private calculateKeywordScore(item: ContentItem, reference: ContentItem): number {
    if (!item.keywords || !reference.keywords) return 0;

    const itemKeywords = new Set(item.keywords.map(k => k.id));
    const refKeywords = new Set(reference.keywords.map(k => k.id));

    if (itemKeywords.size === 0 || refKeywords.size === 0) return 0;

    let matches = 0;
    itemKeywords.forEach(id => { if (refKeywords.has(id)) matches++; });

    // Jaccard similarity
    const union = new Set([...itemKeywords, ...refKeywords]);
    return matches / union.size;
  }

  private diversifyResults(items: ContentItem[]): ContentItem[] {
    if (items.length <= 5) return items;

    const diversified: ContentItem[] = [];
    const genresSeen = new Set<number>();
    const languagesSeen = new Set<string>();

    for (const item of items) {
      const itemGenres = item.genre_ids || item.genres?.map(g => g.id) || [];
      const itemLang = item.original_language;

      // Calculate diversity score
      let diversityPenalty = 0;
      itemGenres.forEach(g => {
        if (genresSeen.has(g)) diversityPenalty += 0.2;
      });
      if (languagesSeen.has(itemLang)) diversityPenalty += 0.1;

      // Add if diverse enough or high scoring
      if (diversityPenalty < this.config.limits.diversityThreshold || item.score > 0.8) {
        diversified.push(item);
        itemGenres.forEach(g => genresSeen.add(g));
        languagesSeen.add(itemLang);
      }

      if (diversified.length >= this.config.limits.maxResults) break;
    }

    return diversified;
  }

  private async getTrendingContent(): Promise<ContentItem[]> {
    const [movies, shows] = await Promise.all([
      tmdb.getTrendingMovies('day'),
      tmdb.getTrendingShows('day'),
    ]);

    return [
      ...movies.results.map((m: any) => ({ ...m, type: 'movie' as const })),
      ...shows.results.map((s: any) => ({ ...s, type: 'tv' as const })),
    ];
  }

  // "recommendations" (viewer co-watch data) leads; "similar" (loose
  // genre/keyword matching) only backfills with well-known, genre-matching
  // titles — its raw output is full of obscure unrelated content
  private async getSimilarContent(content: ContentItem): Promise<ContentItem[]> {
    const [similar, recommendations] = await Promise.all([
      content.type === 'movie'
        ? tmdb.getSimilarMovies(content.id)
        : tmdb.getSimilarShows(content.id),
      content.type === 'movie'
        ? tmdb.getMovieRecommendations(content.id)
        : tmdb.getShowRecommendations(content.id),
    ]);

    const sourceGenres = new Set(
      content.genre_ids || content.genres?.map(g => g.id) || []
    );
    const backfill = similar.results
      .filter((item: any) => (item.vote_count || 0) >= 200)
      .filter((item: any) => (item.vote_average || 0) >= this.config.filters.minRating)
      .filter((item: any) =>
        sourceGenres.size === 0 ||
        (item.genre_ids || []).some((g: number) => sourceGenres.has(g))
      );

    return [
      ...recommendations.results.map((item: any) => ({ ...item, type: content.type })),
      ...backfill.map((item: any) => ({ ...item, type: content.type })),
    ];
  }

  // NEW: Get collaborative filtering recommendations (TMDB recommendations endpoint)
  private async getCollaborativeContent(content: ContentItem): Promise<ContentItem[]> {
    try {
      const recommendations = content.type === 'movie'
        ? await tmdb.getMovieRecommendations(content.id)
        : await tmdb.getShowRecommendations(content.id);

      // Mark these with special score so we can give them collaborative bonus
      return recommendations.results.map((item: any) => ({
        ...item,
        type: content.type,
        score: 999, // Special marker for collaborative filtering
      }));
    } catch (error) {
      return [];
    }
  }

  // NEW: Get keyword-based recommendations
  private async getKeywordBasedContent(content: ContentItem): Promise<ContentItem[]> {
    try {
      // Fetch keywords for the reference content
      const keywordsData = content.type === 'movie'
        ? await tmdb.getMovieKeywords(content.id)
        : await tmdb.getShowKeywords(content.id);

      const keywords = keywordsData.keywords || keywordsData.results || [];
      if (keywords.length === 0) return [];

      // Get top 5 most relevant keywords
      const topKeywords = keywords.slice(0, 5).map((k: any) => k.id).join(',');

      // Use TMDB discover endpoint to find content with matching keywords
      const [movieResults, tvResults] = await Promise.all([
        tmdb.discoverMovies({
          with_keywords: topKeywords,
          sort_by: 'vote_average.desc',
          'vote_count.gte': 100,
        }),
        tmdb.discoverShows({
          with_keywords: topKeywords,
          sort_by: 'vote_average.desc',
          'vote_count.gte': 50,
        }),
      ]);

      return [
        ...movieResults.results.map((m: any) => ({ ...m, type: 'movie' as const, keywords })),
        ...tvResults.results.map((s: any) => ({ ...s, type: 'tv' as const, keywords })),
      ];
    } catch (error) {
      return [];
    }
  }

  // NEW: Get network/studio-based recommendations
  private async getNetworkStudioContent(content: ContentItem): Promise<ContentItem[]> {
    try {
      const results: ContentItem[] = [];

      // For TV shows, discover by network
      if (content.type === 'tv' && content.networks && content.networks.length > 0) {
        const networkIds = content.networks.map(n => n.id).join('|');
        const networkResults = await tmdb.discoverShows({
          with_networks: networkIds,
          sort_by: 'vote_average.desc',
          'vote_count.gte': 50,
        });
        results.push(...networkResults.results.map((s: any) => ({
          ...s,
          type: 'tv' as const,
          networks: content.networks,
        })));
      }

      // For movies, discover by production company
      if (content.type === 'movie' && content.production_companies && content.production_companies.length > 0) {
        const companyIds = content.production_companies.map(c => c.id).join('|');
        const companyResults = await tmdb.discoverMovies({
          with_companies: companyIds,
          sort_by: 'vote_average.desc',
          'vote_count.gte': 100,
        });
        results.push(...companyResults.results.map((m: any) => ({
          ...m,
          type: 'movie' as const,
          production_companies: content.production_companies,
        })));
      }

      return results;
    } catch (error) {
      return [];
    }
  }

  // NEW: Get cast/crew-based recommendations
  private async getCastCrewContent(content: ContentItem): Promise<ContentItem[]> {
    try {
      if (!content.credits) return [];

      const results: ContentItem[] = [];

      // Get director (movies) or creator (TV shows)
      if (content.type === 'movie' && content.credits.crew) {
        const directors = content.credits.crew.filter(c => c.job === 'Director').slice(0, 2);
        for (const director of directors) {
          const directorResults = await tmdb.discoverMovies({
            with_crew: director.id.toString(),
            sort_by: 'vote_average.desc',
            'vote_count.gte': 100,
          });
          results.push(...directorResults.results.map((m: any) => ({
            ...m,
            type: 'movie' as const,
            credits: content.credits,
          })));
        }
      }

      // Get top 3 cast members
      if (content.credits.cast) {
        const topCast = content.credits.cast.slice(0, 3);
        for (const actor of topCast) {
          const castResults = content.type === 'movie'
            ? await tmdb.discoverMovies({
                with_cast: actor.id.toString(),
                sort_by: 'vote_average.desc',
                'vote_count.gte': 100,
              })
            : await tmdb.discoverShows({
                with_cast: actor.id.toString(),
                sort_by: 'vote_average.desc',
                'vote_count.gte': 50,
              });

          results.push(...castResults.results.map((item: any) => ({
            ...item,
            type: content.type,
            credits: content.credits,
          })));
        }
      }

      return results;
    } catch (error) {
      return [];
    }
  }

  private async getGenreBasedContent(): Promise<ContentItem[]> {
    // Get top 3 preferred genres
    const topGenres = Array.from(this.genreAffinities.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([genreId]) => genreId);

    if (topGenres.length === 0) return [];

    const promises = topGenres.flatMap(genreId => [
      tmdb.discoverMovies({
        with_genres: genreId.toString(),
        sort_by: 'vote_average.desc',
        'vote_count.gte': 100,
      }),
      tmdb.discoverShows({
        with_genres: genreId.toString(),
        sort_by: 'vote_average.desc',
        'vote_count.gte': 50,
      }),
    ]);

    const results = await Promise.all(promises);

    const content: ContentItem[] = [];
    results.forEach((result, index) => {
      const type = index % 2 === 0 ? 'movie' : 'tv';
      result.results.forEach((item: any) => {
        content.push({ ...item, type: type as 'movie' | 'tv' });
      });
    });

    return content;
  }

  private async getMixedContent(): Promise<ContentItem[]> {
    const [trending, genre, popular] = await Promise.all([
      this.getTrendingContent(),
      this.getGenreBasedContent(),
      this.getPopularContent(),
    ]);

    // Mix different sources for variety
    const mixed: ContentItem[] = [];
    const sources = [trending, genre, popular];
    let index = 0;

    while (mixed.length < 100 && sources.some(s => s.length > 0)) {
      const source = sources[index % sources.length];
      if (source.length > 0) {
        mixed.push(source.shift()!);
      }
      index++;
    }

    return mixed;
  }

  private async getPopularContent(): Promise<ContentItem[]> {
    const [movies, shows] = await Promise.all([
      tmdb.getPopularMovies(),
      tmdb.getPopularShows(),
    ]);

    return [
      ...movies.results.map((m: any) => ({ ...m, type: 'movie' as const })),
      ...shows.results.map((s: any) => ({ ...s, type: 'tv' as const })),
    ];
  }

  async getLanguageSpecificContent(language: string): Promise<ContentItem[]> {
    const [movies, shows] = await Promise.all([
      tmdb.discoverMovies({
        with_original_language: language,
        sort_by: 'popularity.desc',
      }),
      tmdb.discoverShows({
        with_original_language: language,
        sort_by: 'popularity.desc',
      }),
    ]);

    return [
      ...movies.results.map((m: any) => ({ ...m, type: 'movie' as const })),
      ...shows.results.map((s: any) => ({ ...s, type: 'tv' as const })),
    ];
  }

  async getContinueWatching(userId: string): Promise<ContentItem[]> {
    const interactions = await getUserInteractions(userId);

    // Filter for partially watched content
    const partial = interactions
      .filter(i => i.completionRate > 0.1 && i.completionRate < 0.9)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 10);

    // Fetch full details for each item
    const promises = partial.map(async (interaction) => {
      const data = interaction.type === 'movie'
        ? await tmdb.getMovie(interaction.contentId)
        : await tmdb.getShow(interaction.contentId);
      return { ...data, type: interaction.type, score: 1 };
    });

    return Promise.all(promises);
  }

  async getWatchAgain(userId: string, watchlist: WatchlistItem[]): Promise<ContentItem[]> {
    // Find highly rated completed content
    const rewatchable = watchlist
      .filter(item => item.personalRank && item.personalRank >= 8)
      .sort((a, b) => (b.personalRank || 0) - (a.personalRank || 0))
      .slice(0, 10);

    // Fetch full details
    const promises = rewatchable.map(async (item) => {
      const data = item.type === 'movie'
        ? await tmdb.getMovie(item.id)
        : await tmdb.getShow(item.id);
      return { ...data, type: item.type, score: 0.9, reason: 'You loved this' };
    });

    return Promise.all(promises);
  }

  // Get recommendations based on a specific genre the user likes
  // Uses the TMDB recommendations endpoint from items in the user's watchlist with that genre
  async getGenreBasedRecommendations(genreId: number, genreName: string, watchlist?: WatchlistItem[]): Promise<ContentItem[]> {
    try {
      // Find items in the user's watchlist that have this genre
      const itemsWithGenre = (watchlist || [])
        .filter(item => item.genres?.includes(genreId))
        .sort(() => Math.random() - 0.5)
        .slice(0, 5); // Use up to 5 items as seed for recommendations

      if (itemsWithGenre.length === 0) {
        // Fallback to discover API if no watchlist items match
        const [movieResults, tvResults] = await Promise.all([
          tmdb.discoverMovies({
            with_genres: genreId.toString(),
            sort_by: 'popularity.desc',
            'vote_count.gte': 100,
          }),
          tmdb.discoverShows({
            with_genres: genreId.toString(),
            sort_by: 'popularity.desc',
            'vote_count.gte': 50,
          }),
        ]);

        const results: ContentItem[] = [
          ...movieResults.results.map((m: any) => ({ ...m, type: 'movie' as const, reason: `Because you like ${genreName}` })),
          ...tvResults.results.map((s: any) => ({ ...s, type: 'tv' as const, reason: `Because you like ${genreName}` })),
        ];

        return results
          .filter(item => !this.watchedContentCache.has(item.id))
          .sort(() => Math.random() - 0.5)
          .slice(0, 20);
      }

      // Get recommendations from each item using TMDB's recommendations endpoint
      const allRecommendations: ContentItem[] = [];

      for (const item of itemsWithGenre) {
        try {
          const recs = item.type === 'movie'
            ? await tmdb.getMovieRecommendations(item.id)
            : await tmdb.getShowRecommendations(item.id);

          allRecommendations.push(
            ...recs.results.map((r: any) => ({
              ...r,
              type: item.type,
              reason: `Because you like ${genreName}`,
            }))
          );
        } catch (error) {
          // Continue with other items
        }
      }

      // Filter out watched content, deduplicate, and shuffle
      return allRecommendations
        .filter(item => !this.watchedContentCache.has(item.id))
        .filter((item, index, self) => index === self.findIndex(t => t.id === item.id))
        .sort(() => Math.random() - 0.5)
        .slice(0, 20);
    } catch (error) {
      return [];
    }
  }

  // Get recommendations similar to a specific show/movie the user watched.
  // TMDB's "recommendations" endpoint (what viewers actually watched together)
  // leads; the "similar" endpoint only backfills because its loose
  // genre/keyword matching frequently surfaces obscure, unrelated titles.
  async getSimilarToWatched(item: WatchlistItem): Promise<ContentItem[]> {
    try {
      const [similar, recommendations] = await Promise.all([
        item.type === 'movie'
          ? tmdb.getSimilarMovies(item.id)
          : tmdb.getSimilarShows(item.id),
        item.type === 'movie'
          ? tmdb.getMovieRecommendations(item.id)
          : tmdb.getShowRecommendations(item.id),
      ]);

      const reason = `Because you watched ${item.name}`;

      const primary: ContentItem[] = recommendations.results
        .filter((r: any) => (r.vote_count || 0) >= this.config.filters.minVoteCount)
        .map((r: any) => ({ ...r, type: item.type, reason }));

      // Backfill from "similar" only with well-known titles that share a
      // genre with the source
      const sourceGenres = new Set(item.genres || []);
      const backfill: ContentItem[] = similar.results
        .filter((r: any) => (r.vote_count || 0) >= 200)
        .filter((r: any) => (r.vote_average || 0) >= this.config.filters.minRating)
        .filter((r: any) =>
          sourceGenres.size === 0 ||
          (r.genre_ids || []).some((g: number) => sourceGenres.has(g))
        )
        .map((r: any) => ({ ...r, type: item.type, reason }));

      // Filter out watched content and deduplicate, keeping relevance order
      const uniqueResults = [...primary, ...backfill]
        .filter(r => !this.watchedContentCache.has(r.id))
        .filter((r, index, self) => index === self.findIndex(t => t.id === r.id));

      return uniqueResults.slice(0, 20);
    } catch (error) {
      return [];
    }
  }

  // Get recommendations based on highly rated content
  async getBasedOnHighlyRated(watchlist: WatchlistItem[]): Promise<ContentItem[]> {
    // Find items with high personal ratings (8+)
    const highlyRated = watchlist
      .filter(item => item.personalRank && item.personalRank >= 8)
      .sort((a, b) => (b.personalRank || 0) - (a.personalRank || 0))
      .slice(0, 3);

    if (highlyRated.length === 0) return [];

    const allRecommendations: ContentItem[] = [];

    for (const item of highlyRated) {
      try {
        const recs = item.type === 'movie'
          ? await tmdb.getMovieRecommendations(item.id)
          : await tmdb.getShowRecommendations(item.id);

        allRecommendations.push(
          ...recs.results.map((r: any) => ({
            ...r,
            type: item.type,
            reason: `Because you rated ${item.name} highly`,
          }))
        );
      } catch (error) {
        // Continue with other items
      }
    }

    // Filter and deduplicate
    return allRecommendations
      .filter(item => !this.watchedContentCache.has(item.id))
      .filter((item, index, self) => index === self.findIndex(t => t.id === item.id))
      .slice(0, 20);
  }

  // Get user's top genres with counts
  getTopGenres(watchlist: WatchlistItem[], limit: number = 3): { id: number; name: string; count: number }[] {
    const genreCounts = new Map<number, { name: string; count: number }>();

    // Genre ID to name mapping (TMDB standard genres)
    const genreNames: Record<number, string> = {
      28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy',
      80: 'Crime', 99: 'Documentary', 18: 'Drama', 10751: 'Family',
      14: 'Fantasy', 36: 'History', 27: 'Horror', 10402: 'Music',
      9648: 'Mystery', 10749: 'Romance', 878: 'Sci-Fi', 10770: 'TV Movie',
      53: 'Thriller', 10752: 'War', 37: 'Western',
      // TV specific
      10759: 'Action & Adventure', 10762: 'Kids', 10763: 'News',
      10764: 'Reality', 10765: 'Sci-Fi & Fantasy', 10766: 'Soap',
      10767: 'Talk', 10768: 'War & Politics',
    };

    watchlist.forEach(item => {
      item.genres?.forEach(genreId => {
        const current = genreCounts.get(genreId) || { name: genreNames[genreId] || 'Unknown', count: 0 };
        genreCounts.set(genreId, { name: current.name, count: current.count + 1 });
      });
    });

    return Array.from(genreCounts.entries())
      .map(([id, data]) => ({ id, name: data.name, count: data.count }))
      .filter(g => g.name !== 'Unknown')
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  // Get random items from watchlist for "Because you watched" rows
  getRandomWatchedItems(watchlist: WatchlistItem[], count: number = 3): WatchlistItem[] {
    // Prioritize items with high engagement (personal rank, recently added, currently watching)
    const prioritized = watchlist
      .filter(item => item.personalRank && item.personalRank >= 7)
      .sort(() => Math.random() - 0.5);

    const currentlyWatching = watchlist
      .filter(item => item.currentlyWatching && !prioritized.find(p => p.id === item.id))
      .sort(() => Math.random() - 0.5);

    const others = watchlist
      .filter(item => !prioritized.find(p => p.id === item.id) && !currentlyWatching.find(c => c.id === item.id))
      .sort(() => Math.random() - 0.5);

    return [...prioritized, ...currentlyWatching, ...others].slice(0, count);
  }

  // Watch-history-driven recommendations, YouTube-style:
  // 1. Weight everything the user watched by engagement (rating, actively
  //    watching, how recently they touched it) and pick the strongest seeds.
  // 2. Pull TMDB collaborative recommendations for every seed in parallel.
  // 3. Aggregate — a title surfaced by several things the user watched
  //    outscores one surfaced by a single seed — then blend in the user's
  //    genre taste and title quality.
  async getPersonalizedForYou(watchlist: WatchlistItem[]): Promise<ContentItem[]> {
    const limit = this.config.limits.maxResults;

    if (watchlist.length === 0) {
      const popular = await this.getPopularContent();
      return popular
        .filter(item => !this.watchedContentCache.has(item.id))
        .slice(0, limit);
    }

    // 1. Engagement-weighted seed selection
    const seedPool = watchlist
      .map(item => {
        const ratingWeight = item.personalRank ? item.personalRank / 10 : 0.5;
        const watchingBoost = item.currentlyWatching ? 1.5 : 1;
        const lastEngaged = item.lastWatched || item.addedAt;
        return { item, weight: ratingWeight * watchingBoost * this.getRecencyBoost(lastEngaged) };
      })
      // Content the user rated poorly shouldn't drive recommendations
      .filter(seed => !seed.item.personalRank || seed.item.personalRank >= 5)
      .sort((a, b) => b.weight - a.weight);

    // Strongest signals, plus a couple of random older picks for exploration
    const seeds = [
      ...seedPool.slice(0, 6),
      ...seedPool.slice(6).sort(() => Math.random() - 0.5).slice(0, 2),
    ];

    if (seeds.length === 0) {
      const popular = await this.getPopularContent();
      return popular
        .filter(item => !this.watchedContentCache.has(item.id))
        .slice(0, limit);
    }

    const maxSeedWeight = seeds[0].weight || 1;

    // 2. Collaborative recommendations for every seed, in parallel
    const seedResults = await Promise.allSettled(
      seeds.map(async seed => {
        const recs = seed.item.type === 'movie'
          ? await tmdb.getMovieRecommendations(seed.item.id)
          : await tmdb.getShowRecommendations(seed.item.id);
        return { seed, results: (recs.results || []) as any[] };
      })
    );

    // 3. Aggregate candidates across seeds
    interface Candidate {
      item: ContentItem;
      collaborative: number;
      topSeed: { name: string; weight: number };
    }
    const candidates = new Map<string, Candidate>();

    for (const result of seedResults) {
      if (result.status !== 'fulfilled') continue;
      const { seed, results } = result.value;
      const seedWeight = seed.weight / maxSeedWeight;

      results.forEach((rec: any, rank: number) => {
        if (this.watchedContentCache.has(rec.id)) return;
        // TMDB orders results by relevance; earlier positions count more
        const positionWeight = 1 - (rank / results.length) * 0.5;
        const contribution = seedWeight * positionWeight;
        const key = `${seed.item.type}-${rec.id}`;
        const existing = candidates.get(key);

        if (existing) {
          existing.collaborative += contribution;
          if (seedWeight > existing.topSeed.weight) {
            existing.topSeed = { name: seed.item.name, weight: seedWeight };
          }
        } else {
          candidates.set(key, {
            item: { ...rec, type: seed.item.type },
            collaborative: contribution,
            topSeed: { name: seed.item.name, weight: seedWeight },
          });
        }
      });
    }

    // 4. Blend collaborative strength with genre taste and quality
    const maxCollaborative = Math.max(
      ...Array.from(candidates.values()).map(c => c.collaborative),
      1
    );
    const scored: ContentItem[] = [];

    candidates.forEach(candidate => {
      const { item } = candidate;
      if ((item.vote_count || 0) < this.config.filters.minVoteCount) return;
      if ((item.vote_average || 0) < this.config.filters.minRating) return;

      let score =
        (candidate.collaborative / maxCollaborative) * 0.5 +
        this.calculateGenreScore(item) * 0.2 +
        this.calculateRatingScore(item) * 0.2 +
        this.calculatePopularityScore(item) * 0.1;

      const languageMatch =
        this.config.filters.languagePreferred.includes(item.original_language) ||
        this.userLanguages.includes(item.original_language);
      if (!languageMatch) {
        score *= 0.7;
      }

      scored.push({
        ...item,
        score,
        reason: `Because you watched ${candidate.topSeed.name}`,
      });
    });

    scored.sort((a, b) => (b.score || 0) - (a.score || 0));

    // 5. Keep the movie/TV balance close to the user's own viewing mix
    const tvShare = watchlist.filter(i => i.type === 'tv').length / watchlist.length;
    const tvTarget = Math.round(limit * Math.min(Math.max(tvShare, 0.2), 0.8));
    const tvItems = scored.filter(i => i.type === 'tv');
    const movieItems = scored.filter(i => i.type === 'movie');

    const picked = [
      ...tvItems.slice(0, tvTarget),
      ...movieItems.slice(0, limit - tvTarget),
    ];

    // Backfill from the overall ranking if one type ran short
    if (picked.length < limit) {
      const pickedKeys = new Set(picked.map(i => `${i.type}-${i.id}`));
      for (const item of scored) {
        if (picked.length >= limit) break;
        if (!pickedKeys.has(`${item.type}-${item.id}`)) {
          picked.push(item);
          pickedKeys.add(`${item.type}-${item.id}`);
        }
      }
    }

    // Sparse watchlists can produce thin results — top up with trending
    if (picked.length < 10) {
      try {
        const trending = await this.getTrendingContent();
        const pickedKeys = new Set(picked.map(i => `${i.type}-${i.id}`));
        for (const item of trending) {
          if (picked.length >= limit) break;
          if (this.watchedContentCache.has(item.id)) continue;
          if (pickedKeys.has(`${item.type}-${item.id}`)) continue;
          picked.push({ ...item, score: 0.05, reason: 'Trending now' });
          pickedKeys.add(`${item.type}-${item.id}`);
        }
      } catch (error) {
        // Trending top-up is best-effort
      }
    }

    picked.sort((a, b) => (b.score || 0) - (a.score || 0));
    return picked.slice(0, limit);
  }
}

// Singleton instance
let engineInstance: RecommendationEngine | null = null;

export function getRecommendationEngine(config?: Partial<RecommendationConfig>): RecommendationEngine {
  if (!engineInstance) {
    engineInstance = new RecommendationEngine(config);
  }
  return engineInstance;
}